import { FC, useCallback, useEffect, useRef, useState } from "react";
import "../../styles/components.css";
// the toolbar and rail panels are the model viewer's, so their styles are too
import "../../styles/standard-viewer.css";
import "../../styles/pdf-viewer-library.css";
import "../../styles/pdf-viewer-editor-library.css";
import "../../styles/pdf-viewer.css";
import { useAppStateContext } from "../../middleware/context/state-context-provider";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  ApplyUserLocation,
  BuildCanvasCrumbs,
  BuildLocationPath,
  CheckAuthentication,
  HandleLogOut,
  SelectCrumbUser,
  StartBrowserDownload,
  UpdateUserLastActionDate,
} from "../../helper/ui/methods";
import { FileFullName, FormatDateBE } from "../../helper/general/methods";
import ResponseDialog from "../ui/user/response-dialog";
import DecisionDialog from "../ui/user/user-binary-decision-dialog";
import { PdfViewport } from "../ui/viewer/pdf-viewport";
import { PdfToolbar } from "../ui/viewer/pdf-toolbar";
import { PdfPagesMenu } from "../ui/viewer/pdf-pages-menu";
import { PdfOutlineMenu } from "../ui/viewer/pdf-outline-menu";
import { PdfSearchMenu } from "../ui/viewer/pdf-search-menu";
import { PdfMeasureMenu } from "../ui/viewer/pdf-measure-menu";
import {
  PdfMarkupMenu,
  highlightColors,
  markupColors,
} from "../ui/viewer/pdf-markup-menu";
import {
  PdfCompareMenu,
  CompareTargetValue,
} from "../ui/viewer/pdf-compare-menu";
import { PdfHandler } from "../../middleware/handlers/pdf-handler";
import { PdfRetrieve } from "../../core/database/file/pdf-retrieve";
import type {
  PdfMarkupStyle,
  PdfMarkupTool,
  PdfPointerMode,
  PdfViewerState,
} from "../../core/pdf/pdf-scene";
import type { FileUploadInterface } from "../../middleware/interfaces/file/file-upload-interface";
import type { PdfRetrieveInterface } from "../../middleware/interfaces/file/pdf-retrieve-interface";
import type { UserDecisionInterface } from "../../middleware/interfaces/user/user-decision-interface";
import type {
  PdfMeasureTool,
  PdfMeasureUnits,
} from "../../core/pdf/pdf-measure";
import type { PdfCompareMode } from "../../core/pdf/pdf-compare";
import MenuButton from "../ui/user/menu-button";
import MainNavigationPanel from "../ui/user/main-navigation-panel";
import MainFooterPanel from "../ui/user/main-footer-panel";
import CanvasHeader from "../ui/user/canvas-header";

type PdfViewerPanel =
  | "PAGES"
  | "OUTLINE"
  | "SEARCH"
  | "MEASURE"
  | "MARKUP"
  | "COMPARE";

const initialViewerState: PdfViewerState = {
  pageNumber: 1,
  pageCount: 0,
  scale: 1,
  rotation: 0,
  searchMatches: { current: 0, total: 0 },
  searchNotFound: false,
  measurements: [],
  pageScales: {},
  calibration: null,
  markupModified: false,
  sheetNumbers: {},
  sheetsRead: false,
};

export const PdfViewer: FC = () => {
  const [state, dispatch] = useAppStateContext();
  const navigate = useNavigate();
  const location = useLocation();
  // one panel docks at a time, so the document keeps the rest of the canvas
  const [openPanel, setOpenPanel] = useState<PdfViewerPanel | null>(null);
  const [viewerState, setViewerState] =
    useState<PdfViewerState>(initialViewerState);
  // Like the report viewer's maximize: the menus step aside and the document
  // fills the browser window.
  const [pdfMaximize, setPdfMaximize] = useState(false);
  const [pdfPointerMode, setPdfPointerMode] = useState<PdfPointerMode>("PAN");
  const [measureTool, setMeasureTool] = useState<PdfMeasureTool>("LENGTH");
  const [measureUnits, setMeasureUnits] = useState<PdfMeasureUnits>("IMPERIAL");
  const [markupTool, setMarkupTool] = useState<PdfMarkupTool>("PEN");
  const [markupStyle, setMarkupStyle] = useState<PdfMarkupStyle>({
    color: markupColors[0].color,
    thickness: 3,
    fontSize: 14,
  });
  const [highlightColor, setHighlightColor] = useState(
    highlightColors[0].color,
  );
  const [compareMode, setCompareMode] =
    useState<PdfCompareMode>("SIDE_BY_SIDE");
  // "fileID:version" of the PDF compared against
  const [compareTarget, setCompareTarget] = useState("");
  const [comparePageCount, setComparePageCount] = useState(0);
  const [comparePageOffset, setComparePageOffset] = useState(0);
  const [compareFade, setCompareFade] = useState(0.5);
  const [compareStatus, setCompareStatus] = useState<string | null>(null);
  // the version count when a save went out, until the new version lands
  const [markupSaveFrom, setMarkupSaveFrom] = useState<number | null>(null);
  // where to go once unsaved markups are confirmed discarded
  const pendingLeave = useRef<(() => void) | null>(null);
  // the address is read for a PDF to reopen once per visit to the viewer
  const pdfAddressRead = useRef(false);
  // shown over the document while a print is being prepared
  const [pdfStatus, setPdfStatus] = useState<string | null>(null);

  // USER-role members may look at a PDF but not take it away, on paper or as
  // a file, the same as everywhere else in the app.
  const pdfTakeAwayAllowed =
    state.user?.openProject?.projectUserRole !== "USER";
  // and cannot upload, so their markups cannot be saved as a version either
  const pdfMarkupSaveAllowed = pdfTakeAwayAllowed;

  const pdfFileName = state.openPdf?.["pdfName"]
    ? FileFullName(state.openPdf["pdfName"], "pdf")
    : "";

  // "· v2 of 3" when the file has more than one version, so an older one is
  // never mistaken for the latest
  const pdfFile = state.user?.openProject?.projectFiles.find(
    (file) => file.fileID === state.openPdf?.["pdfFileID"],
  );
  const pdfVersionCount = pdfFile
    ? Object.keys(pdfFile.fileVersions).length
    : 0;
  const pdfVersion: number = state.openPdf?.["pdfVersion"] ?? 0;
  const pdfVersionLabel =
    pdfVersionCount > 1 && pdfVersion
      ? ` · v${pdfVersion} of ${pdfVersionCount}`
      : "";

  const TogglePanel = (panel: PdfViewerPanel) => {
    setOpenPanel(openPanel === panel ? null : panel);
  };

  // pdf.js reports page, zoom and search changes one field at a time
  const HandleViewerChange = useCallback(
    (viewerChange: Partial<PdfViewerState>) => {
      setViewerState((previous) => ({ ...previous, ...viewerChange }));
    },
    [],
  );

  // As in the model viewer, measuring owns the clicks only while its panel is
  // open; closing it hands them back to panning or selecting.
  useEffect(() => {
    PdfHandler.SetMeasureTool(openPanel === "MEASURE" ? measureTool : null);
  }, [openPanel, measureTool]);

  useEffect(() => {
    PdfHandler.SetMeasureUnits(measureUnits);
  }, [measureUnits]);

  // Markup works the same way: pdf.js's editors have the mouse only while the
  // panel is open. pageCount is there so a PDF that opens with the panel
  // already open gets the tool too.
  useEffect(() => {
    PdfHandler.SetMarkupTool(openPanel === "MARKUP" ? markupTool : null);
  }, [openPanel, markupTool, viewerState.pageCount]);

  useEffect(() => {
    PdfHandler.SetMarkupStyle(markupStyle);
  }, [markupStyle, viewerState.pageCount]);

  useEffect(() => {
    PdfHandler.SetHighlightColor(highlightColor);
  }, [highlightColor, viewerState.pageCount]);

  // Opening a PDF suggests comparing it with the version before it, or with
  // the one after when it is the first.
  useEffect(() => {
    const versions = pdfFile
      ? Object.keys(pdfFile.fileVersions).map(Number)
      : [];
    const suggested = versions.includes(pdfVersion - 1)
      ? pdfVersion - 1
      : versions.includes(pdfVersion + 1)
        ? pdfVersion + 1
        : null;
    setCompareTarget(
      pdfFile && suggested ? CompareTargetValue(pdfFile.fileID, suggested) : "",
    );
    setComparePageOffset(0);
  }, [state.openPdf?.["pdfFileID"], pdfVersion]);

  // The PDF compared against is fetched the way the open one is, but only
  // into the comparison; the open PDF and the path bar stay as they are.
  useEffect(() => {
    if (openPanel !== "COMPARE" || !compareTarget || !state.user?.openProject) {
      PdfHandler.CloseCompare();
      setComparePageCount(0);
      setCompareStatus(null);
      return;
    }

    let abandoned = false;
    const [fileID, fileActiveVersion] = compareTarget.split(":").map(Number);
    setCompareStatus("Opening the PDF to compare with...");

    PdfRetrieve.Retrieve({
      ...state,
      data: {
        projectID: state.user.openProject.projectID,
        fileID,
        fileActiveVersion,
        type: "PdfRetrieveInterface",
      },
    })
      .then((serverResponse: any) => {
        if (typeof serverResponse === "string") {
          throw new Error(serverResponse);
        } else if (serverResponse?.status === "error") {
          throw new Error(serverResponse.message);
        }
        return PdfHandler.OpenCompare(serverResponse.pdfFileUrl);
      })
      .then((pageCount) => {
        if (abandoned) return;
        setComparePageCount(pageCount);
        setCompareStatus(null);
      })
      .catch((error: any) => {
        if (abandoned) return;
        console.error("Error opening PDF to compare:", error);
        setComparePageCount(0);
        setCompareStatus(
          error?.name === "PasswordException"
            ? "That PDF is password protected and cannot be compared."
            : "Failed to open that PDF. Please try again.",
        );
      });

    return () => {
      abandoned = true;
    };
  }, [openPanel, compareTarget]);

  // Like the tools, the comparison shows only while its panel is open.
  useEffect(() => {
    PdfHandler.SetCompareMode(openPanel === "COMPARE" ? compareMode : null);
  }, [openPanel, compareMode]);

  useEffect(() => {
    PdfHandler.SetComparePageOffset(comparePageOffset);
  }, [comparePageOffset]);

  useEffect(() => {
    PdfHandler.SetCompareFade(compareFade);
  }, [compareFade]);

  // what each half of a side-by-side comparison shows
  const [compareFileID, compareVersion] = compareTarget.split(":").map(Number);
  const compareFile = state.user?.openProject?.projectFiles.find(
    (file) => file.fileID === compareFileID,
  );
  const compareLabels: [string, string] | null =
    openPanel === "COMPARE" &&
    compareMode === "SIDE_BY_SIDE" &&
    compareFile &&
    comparePageCount > 0 &&
    compareStatus === null
      ? [
          `${pdfFileName} · v${pdfVersion}`,
          `${FileFullName(compareFile.fileName, compareFile.fileType)} · v${compareVersion}`,
        ]
      : null;

  // The marked-up PDF goes up through the ordinary upload, under the file's
  // own name in its own folder, which the server files as its next version.
  const HandleMarkupSave = async () => {
    if (!pdfFile || !state.user?.openProject || markupSaveFrom !== null) return;

    try {
      const pdfData = await PdfHandler.SaveWithMarkup();
      if (!pdfData) return;

      const uploadFileData: FileUploadInterface = {
        email: state.user.email,
        projectID: state.user.openProject.projectID!,
        projectUserRole: state.user.openProject.projectUserRole,
        fileParentID: pdfFile.fileParentID,
        fileName: pdfFile.fileName,
        fileType: "pdf",
        fileSize: pdfData.byteLength,
        fileUploadDate: FormatDateBE(new Date().toISOString()),
        fileData: new File([pdfData as BlobPart], pdfFileName, {
          type: "application/pdf",
        }),
        type: "FileUploadInterface",
      };
      setMarkupSaveFrom(pdfVersionCount);
      dispatch({ type: "UPLOAD_FILE", payload: uploadFileData });
    } catch (error) {
      console.error("Error saving PDF markups:", error);
      dispatch({
        type: "RESPONSE",
        response: "Failed to save the markups. Please try again.",
      });
    }
  };

  // Once the new version is in the project, it is opened in place of the one
  // that was marked up, so the path bar and the markups are the saved ones.
  useEffect(() => {
    if (markupSaveFrom === null || !pdfFile) return;

    if (pdfVersionCount > markupSaveFrom) {
      setMarkupSaveFrom(null);
      dispatch({
        type: "RETRIEVE_PDF",
        payload: {
          ...pdfFile,
          fileActiveVersion: pdfVersionCount,
          type: "PdfRetrieveInterface",
        } as PdfRetrieveInterface,
      });
    }
  }, [pdfVersionCount]);

  // an upload the server refused ends with a message and no new version;
  // "WAIT" is only the request going out
  useEffect(() => {
    if (
      markupSaveFrom !== null &&
      state.response &&
      state.response !== "WAIT"
    ) {
      setMarkupSaveFrom(null);
    }
  }, [state.response]);

  // Leaving with unsaved markups asks first, in the app's own dialog.
  const GuardLeave = (leave: () => void) => {
    if (!viewerState.markupModified) {
      leave();
      return;
    }

    pendingLeave.current = leave;
    const decision: UserDecisionInterface = {
      title: "Discard your markups?",
      message: pdfMarkupSaveAllowed
        ? "They are not saved. Use Save as new version in the Markup panel to keep them."
        : "Your role in this project can't save markups, so they go when you leave.",
      confirmLabel: "Discard",
      decisionOptions: ["DISCARD_PDF_MARKUP", "NO_ACTION"],
      result: "",
      type: "UserDecisionInterface",
    };
    dispatch({ type: "DECISION", decision: decision });
  };

  useEffect(() => {
    if (!state.decision || state.decision.result === "") return;
    if (!state.decision.decisionOptions.includes("DISCARD_PDF_MARKUP")) return;

    const leave = pendingLeave.current;
    pendingLeave.current = null;
    dispatch({ type: "DECISION", decision: null });
    if (state.decision.result === "DISCARD_PDF_MARKUP" && leave) {
      leave();
    }
  }, [state.decision]);

  // closing or reloading the tab gets the browser's own warning
  useEffect(() => {
    if (!viewerState.markupModified) return;

    const HandleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", HandleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", HandleBeforeUnload);
    };
  }, [viewerState.markupModified]);

  const HandlePointerModeChange = (pointerMode: PdfPointerMode) => {
    PdfHandler.SetPointerMode(pointerMode);
    setPdfPointerMode(pointerMode);
  };

  // The bytes are already in the viewer, so the file is saved from there
  // rather than fetched again through the downloader.
  const HandlePdfDownload = async () => {
    try {
      const pdfData = await PdfHandler.GetData();
      if (!pdfData) return;

      const downloadUrl = URL.createObjectURL(
        new Blob([pdfData as BlobPart], { type: "application/pdf" }),
      );
      StartBrowserDownload(downloadUrl, pdfFileName);
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 60000);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      dispatch({
        type: "RESPONSE",
        response: "Failed to download the PDF. Please try again.",
      });
    }
  };

  // Every page is drawn into an image on a sheet of its own, which is all the
  // print stylesheet lets the browser see; it is removed once the print
  // dialog closes.
  const HandlePdfPrint = async () => {
    if (pdfStatus !== null) return;

    const printContainer = document.createElement("div");
    printContainer.id = "pdfPrintContainer";

    try {
      const pageImages = await PdfHandler.RenderPrintPages(
        (pagesDone, pageCount) => {
          setPdfStatus(`Preparing print... ${pagesDone} / ${pageCount}`);
        },
      );

      for (const pageImage of pageImages) {
        const printPage = document.createElement("div");
        printPage.className = "pdfPrintPage";
        const printImage = document.createElement("img");
        printImage.src = pageImage;
        printPage.appendChild(printImage);
        printContainer.appendChild(printPage);
      }
      document.body.appendChild(printContainer);
      await Promise.all(
        Array.from(printContainer.querySelectorAll("img")).map((printImage) =>
          printImage.decode(),
        ),
      );

      setPdfStatus(null);
      window.addEventListener("afterprint", () => printContainer.remove(), {
        once: true,
      });
      window.print();
    } catch (error) {
      console.error("Error printing PDF:", error);
      printContainer.remove();
      setPdfStatus(null);
      dispatch({
        type: "RESPONSE",
        response: "Failed to prepare the PDF for printing. Please try again.",
      });
    }
  };

  // Like the model viewer, this sits outside the address-driven pages, so the
  // folder it was opened from is the entry before it.
  const HandleViewerReturn = () => {
    GuardLeave(() => {
      if (location.key !== "default") {
        navigate(-1);
      } else {
        navigate(BuildLocationPath(state.user!), { replace: true });
      }
    });
  };

  // Moving the address is enough: LocationHandler opens what it points at.
  const HandleViewerCrumbSelect = (crumbIndex: number) => {
    GuardLeave(() => {
      navigate(BuildLocationPath(SelectCrumbUser(state.user!, crumbIndex)));
    });
  };

  useEffect(() => {
    if (state.user === null || state.user === undefined) {
      console.log("CHECKING AUTHENTICATION!");
      CheckAuthentication(dispatch);
    }
  }, []);

  useEffect(() => {
    if (state.user) {
      UpdateUserLastActionDate(state.user, dispatch);
    }
  }, [state.user]);

  useEffect(() => {
    if (!pdfMaximize) return;

    const HandleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPdfMaximize(false);
      }
    };
    window.addEventListener("keydown", HandleKeyDown);

    return () => {
      window.removeEventListener("keydown", HandleKeyDown);
    };
  }, [pdfMaximize]);

  // a newly opened PDF starts on page one with nothing found
  useEffect(() => {
    setViewerState(initialViewerState);
  }, [state.openPdf]);

  // Like the report viewer, the address says what is open: the project, the
  // file and its version. A refresh, Back and Forward, or a copied link then
  // open the same PDF again.
  useEffect(() => {
    if (!state.openPdf?.["pdfFileUrl"] || !state.user?.openProject) return;

    const pdfPath = `/PDF?project=${state.user.openProject.projectID}&file=${state.openPdf["pdfFileID"]}&version=${state.openPdf["pdfVersion"]}`;
    if (location.pathname + location.search !== pdfPath) {
      navigate(pdfPath, { replace: true });
    }
  }, [state.openPdf]);

  // Arriving with nothing open, as after a refresh, opens what the address
  // names, in its own project and folder so the path bar and Return lead
  // there. A file that is gone, or not the user's to see, goes back to the
  // project instead.
  useEffect(() => {
    if (!state.user || state.openPdf || pdfAddressRead.current) return;
    pdfAddressRead.current = true;

    const parameters = new URLSearchParams(location.search);
    const projectID = parameters.get("project");
    const fileID = parameters.get("file");
    if (!projectID || !fileID) return;

    const project = state.user.projects.find(
      (project) => String(project.projectID) === projectID,
    );
    const file = project?.projectFiles.find(
      (file) =>
        String(file.fileID) === fileID && file.fileType.toLowerCase() === "pdf",
    );
    if (!file) {
      navigate(BuildLocationPath(state.user), { replace: true });
      return;
    }

    const version = Number(parameters.get("version"));
    ApplyUserLocation(
      {
        projectID,
        folderID: file.fileParentID ? String(file.fileParentID) : null,
        type: "UserLocationInterface",
      },
      state,
      dispatch,
    );
    dispatch({
      type: "RETRIEVE_PDF",
      payload: {
        ...file,
        fileActiveVersion: file.fileVersions[version]
          ? version
          : file.fileActiveVersion,
        type: "PdfRetrieveInterface",
      } as PdfRetrieveInterface,
    });
  }, [state.user]);

  return (
    <>
      {(() => {
        if (state.user !== null && state.user !== undefined) {
          return (
            <div
              id="pageWrapper"
              onClick={() => UpdateUserLastActionDate(state.user!, dispatch)}
            >
              {!pdfMaximize && <MainNavigationPanel user={state.user} />}
              <div
                id="contentWrapper"
                className={pdfMaximize ? "pdfViewerMaximized" : undefined}
              >
                {!pdfMaximize && (
                  <div id="leftMenu">
                    <div className="menuButtonWrapper">
                      <MenuButton
                        imageSource="assets/icons/pages.png"
                        alternativeText="PAGES"
                        dispatch={() => {
                          TogglePanel("PAGES");
                        }}
                      />
                      <MenuButton
                        imageSource="assets/icons/outline.png"
                        alternativeText="OUTLINE"
                        dispatch={() => {
                          TogglePanel("OUTLINE");
                        }}
                      />
                      <MenuButton
                        imageSource="assets/icons/search.png"
                        alternativeText="SEARCH"
                        dispatch={() => {
                          TogglePanel("SEARCH");
                        }}
                      />
                      <MenuButton
                        imageSource="assets/icons/measure.png"
                        alternativeText="MEASURE"
                        dispatch={() => {
                          TogglePanel("MEASURE");
                        }}
                      />
                      <MenuButton
                        imageSource="assets/icons/markup.png"
                        alternativeText="MARKUP"
                        dispatch={() => {
                          TogglePanel("MARKUP");
                        }}
                      />
                      <MenuButton
                        imageSource="assets/icons/compare.png"
                        alternativeText="COMPARE"
                        dispatch={() => {
                          TogglePanel("COMPARE");
                        }}
                      />
                      <MenuButton
                        imageSource="assets/icons/return.png"
                        alternativeText="RETURN"
                        dispatch={HandleViewerReturn}
                      />
                      <MenuButton
                        imageSource="assets/icons/logout.png"
                        alternativeText="LOG OUT"
                        dispatch={() => {
                          GuardLeave(() => HandleLogOut(dispatch));
                        }}
                      />
                    </div>
                  </div>
                )}
                <div id="content" className="canvasSurface">
                  <CanvasHeader
                    crumbs={
                      pdfFileName
                        ? [
                            ...BuildCanvasCrumbs(state.user),
                            pdfFileName + pdfVersionLabel,
                          ]
                        : BuildCanvasCrumbs(state.user)
                    }
                    onCrumbSelect={HandleViewerCrumbSelect}
                  />
                  <PdfViewport
                    onViewerChange={HandleViewerChange}
                    pdfStatus={pdfStatus}
                    compareLabels={compareLabels}
                  />
                  {viewerState.pageCount > 0 ? (
                    <PdfToolbar
                      pageNumber={viewerState.pageNumber}
                      pageCount={viewerState.pageCount}
                      scale={viewerState.scale}
                      sheetNumbers={viewerState.sheetNumbers}
                      pdfPointerMode={pdfPointerMode}
                      onPointerModeChange={HandlePointerModeChange}
                      onPrint={pdfTakeAwayAllowed ? HandlePdfPrint : undefined}
                      onDownload={
                        pdfTakeAwayAllowed ? HandlePdfDownload : undefined
                      }
                      pdfMaximize={pdfMaximize}
                      setPdfMaximize={setPdfMaximize}
                    />
                  ) : (
                    <></>
                  )}
                  <ResponseDialog
                    user={state.user}
                    title="Server Response"
                    message={state.response}
                    progress={state.uploadProgress}
                    archiveProgress={state.archiveProgress}
                    dispatch={dispatch}
                  />
                  <DecisionDialog
                    decision={state.decision}
                    dispatch={dispatch}
                  />
                </div>
                {!pdfMaximize && (
                  <div id="rightMenu">
                    <PdfPagesMenu
                      pagesPanelReveal={openPanel === "PAGES"}
                      pageNumber={viewerState.pageNumber}
                      pageCount={viewerState.pageCount}
                      rotation={viewerState.rotation}
                      sheetNumbers={viewerState.sheetNumbers}
                    />
                    <PdfOutlineMenu
                      outlinePanelReveal={openPanel === "OUTLINE"}
                      pageCount={viewerState.pageCount}
                      pageNumber={viewerState.pageNumber}
                      sheetNumbers={viewerState.sheetNumbers}
                      sheetsRead={viewerState.sheetsRead}
                    />
                    <PdfSearchMenu
                      searchPanelReveal={openPanel === "SEARCH"}
                      searchMatches={viewerState.searchMatches}
                      searchNotFound={viewerState.searchNotFound}
                    />
                    <PdfMeasureMenu
                      measurePanelReveal={openPanel === "MEASURE"}
                      measureTool={measureTool}
                      setMeasureTool={setMeasureTool}
                      measureUnits={measureUnits}
                      setMeasureUnits={setMeasureUnits}
                      pageNumber={viewerState.pageNumber}
                      measurements={viewerState.measurements}
                      pageScales={viewerState.pageScales}
                      calibration={viewerState.calibration}
                    />
                    <PdfMarkupMenu
                      markupPanelReveal={openPanel === "MARKUP"}
                      markupTool={markupTool}
                      setMarkupTool={setMarkupTool}
                      markupStyle={markupStyle}
                      setMarkupStyle={setMarkupStyle}
                      highlightColor={highlightColor}
                      setHighlightColor={setHighlightColor}
                      markupModified={viewerState.markupModified}
                      onSave={
                        pdfMarkupSaveAllowed ? HandleMarkupSave : undefined
                      }
                      saving={markupSaveFrom !== null}
                    />
                    <PdfCompareMenu
                      comparePanelReveal={openPanel === "COMPARE"}
                      compareMode={compareMode}
                      setCompareMode={setCompareMode}
                      compareTarget={compareTarget}
                      setCompareTarget={setCompareTarget}
                      pdfFile={pdfFile}
                      pdfVersion={pdfVersion}
                      projectFiles={state.user.openProject?.projectFiles ?? []}
                      projectFolders={
                        state.user.openProject?.projectFolders ?? []
                      }
                      pageNumber={viewerState.pageNumber}
                      comparePageCount={comparePageCount}
                      comparePageOffset={comparePageOffset}
                      setComparePageOffset={setComparePageOffset}
                      compareFade={compareFade}
                      setCompareFade={setCompareFade}
                      compareStatus={compareStatus}
                    />
                  </div>
                )}
              </div>
              {!pdfMaximize && <MainFooterPanel />}
            </div>
          );
        } else {
          if (state.response !== "") {
            return (
              <div
                id="content"
                onClick={() => UpdateUserLastActionDate(state.user!, dispatch)}
              >
                <ResponseDialog
                  user={state.user!}
                  title="Server Response"
                  message={state.response}
                  progress={state.uploadProgress}
                  archiveProgress={state.archiveProgress}
                  dispatch={dispatch}
                />
              </div>
            );
          } else {
            return <Navigate to={"/authentication&registration"} />;
          }
        }
      })()}
    </>
  );
};
